/**
 * Admin Testing Dashboard
 * Comprehensive testing functionality for bookings, emails, and lifecycle simulation
 */

import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/admin/ui/tabs'
import { FlaskConical, Mail, PlayCircle } from 'lucide-react'
import { TestBookingForm } from '@/components/admin/testing/TestBookingForm'
import { TestBookingList } from '@/components/admin/testing/TestBookingList'
import { EmailTester } from '@/components/admin/testing/EmailTester'
import { SimulationPanel } from '@/components/admin/testing/SimulationPanel'
import { useAdminAuth } from '@/hooks/useAdminAuth'

const Testing = () => {
  const { user } = useAdminAuth()
  const [refreshKey, setRefreshKey] = useState(0)

  const handleBookingCreated = () => {
    // Trigger refresh of booking list
    setRefreshKey(prev => prev + 1)
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Testing Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Test bookings, emails, and lifecycle stages without affecting production data
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FlaskConical className="h-4 w-4" />
          <span>Test Mode</span>
        </div>
      </div>

      <Tabs defaultValue="bookings" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="bookings" className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4" />
            Test Bookings
          </TabsTrigger>
          <TabsTrigger value="emails" disabled className="flex items-center gap-2 opacity-50 cursor-not-allowed">
            <Mail className="h-4 w-4" />
            Email Tester
          </TabsTrigger>
          <TabsTrigger value="simulation" className="flex items-center gap-2">
            <PlayCircle className="h-4 w-4" />
            Simulation
          </TabsTrigger>
        </TabsList>

        {/* Test Bookings Tab */}
        <TabsContent value="bookings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Create Test Booking</CardTitle>
              <CardDescription>
                Create a test booking that can be advanced through all lifecycle stages.
                Test bookings are marked with is_test flag and won't appear in production views.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TestBookingForm onBookingCreated={handleBookingCreated} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Test Bookings</CardTitle>
              <CardDescription>
                Manage existing test bookings. Advance stages, trigger emails, or delete.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TestBookingList key={refreshKey} adminEmail={user?.email || ''} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Tester Tab */}
        <TabsContent value="emails">
          <Card>
            <CardHeader>
              <CardTitle>Email Tester</CardTitle>
              <CardDescription>
                Send test emails directly to your inbox ({user?.email}).
                Select an email type, fill in sample data, and test the email delivery.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EmailTester adminEmail={user?.email || ''} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Simulation Tab */}
        <TabsContent value="simulation">
          <Card>
            <CardHeader>
              <CardTitle>Booking Lifecycle Simulation</CardTitle>
              <CardDescription>
                Step through the booking lifecycle stages and see what actions/emails
                are triggered at each stage.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SimulationPanel adminEmail={user?.email || ''} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default Testing
